import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes
const WARNING_BEFORE = 2 * 60 * 1000; // Show warning 2 minutes before

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [subscriptionValid, setSubscriptionValid] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
          setSubscriptionValid(response.data.subscription_valid);
          setSubscriptionStatus(response.data.subscription_status);
          setTrialEndsAt(response.data.trial_ends_at);
        } catch (error) {
          localStorage.removeItem('token');
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setSubscriptionStatus(userData.subscription_status);
    setTrialEndsAt(userData.trial_ends_at);
    
    // Check subscription status
    const subCheck = await axios.get(`${API}/subscription/status`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setSubscriptionValid(subCheck.data.valid);
    
    return userData;
  };

  const loginChild = async (username, password) => {
    const response = await axios.post(`${API}/auth/child-login`, { 
      username, 
      password 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setSubscriptionStatus(userData.subscription_status);
    setTrialEndsAt(userData.trial_ends_at);
    
    // Check subscription status
    const subCheck = await axios.get(`${API}/subscription/status`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setSubscriptionValid(subCheck.data.valid);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    setSubscriptionStatus(newUser.subscription_status);
    setTrialEndsAt(newUser.trial_ends_at);
    setSubscriptionValid(true); // New users have trial
    return newUser;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSubscriptionValid(true);
    setSubscriptionStatus(null);
    setShowInactivityWarning(false);
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(warningTimerRef.current);
  }, []);

  // Inactivity auto-logout
  const resetInactivityTimer = useCallback(() => {
    if (!token) return;
    setShowInactivityWarning(false);
    clearTimeout(warningTimerRef.current);
    clearTimeout(inactivityTimerRef.current);
    
    // Warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowInactivityWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
    
    // Logout timer
    inactivityTimerRef.current = setTimeout(() => {
      logout();
      window.location.href = '/login?reason=inactivity';
    }, INACTIVITY_TIMEOUT);
  }, [token, logout]);

  const dismissInactivityWarning = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (!token) return;
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetInactivityTimer();
    
    events.forEach(e => document.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();
    
    return () => {
      events.forEach(e => document.removeEventListener(e, handler));
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [token, resetInactivityTimer]);

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const updateLanguage = async (language) => {
    try {
      await axios.put(`${API}/auth/language?language=${language}`, {}, {
        headers: getAuthHeader()
      });
      setUser(prev => ({ ...prev, preferred_language: language }));
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  const refreshSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeader()
      });
      setSubscriptionValid(response.data.valid);
      setSubscriptionStatus(response.data.status);
      return response.data;
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token,
      loading, 
      login,
      loginChild,
      register, 
      logout, 
      getAuthHeader,
      updateLanguage,
      isAuthenticated: !!user,
      subscriptionValid,
      subscriptionStatus,
      trialEndsAt,
      refreshSubscription,
      showInactivityWarning,
      dismissInactivityWarning
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
